import { useEffect, useState } from "react";
import { useUiStore, type AppView } from "../state/ui-store.js";
import { useT } from "../i18n/use-t.js";
import type { TKey } from "../i18n/strings.js";
import { LOCALES, normalizeLocale } from "../i18n/locales.js";
import { storage } from "../state/storage.js";
import type { Settings } from "@roqeet/core";
import { MessageSquare, User, Gamepad2, Image as ImageIconLucide, Book, Users, HelpCircle, Settings as SettingsIcon, Globe } from "lucide-react";

/**
 * Navigation sidebar. On mobile it lives in the off-canvas drawer; on md+ it's
 * a persistent column (decision #11). Selecting an item closes the drawer.
 * 라벨은 i18n 키로 두고 useT로 현재 언어에 맞게 표시한다.
 *
 * 언어 선택기는 사이드바 하단에 **항상 보이게** 둔다 — 외국인이 한국어 설정을 헤집으며
 * 언어를 찾지 않도록(설정 안이 아니라 밖으로 노출). 가이드도 nav 항목으로 둔다.
 */
const NAV: { view: AppView; labelKey: TKey; icon: () => JSX.Element }[] = [
  { view: "chats", labelKey: "nav.chats", icon: () => <MessageSquare size={18} aria-hidden="true" /> },
  { view: "characters", labelKey: "nav.characters", icon: () => <User size={18} aria-hidden="true" /> },
  { view: "games", labelKey: "nav.games", icon: () => <Gamepad2 size={18} aria-hidden="true" /> },
  { view: "gallery", labelKey: "nav.gallery", icon: () => <ImageIconLucide size={18} aria-hidden="true" /> },
  { view: "lorebooks", labelKey: "nav.lorebooks", icon: () => <Book size={18} aria-hidden="true" /> },
  { view: "hub", labelKey: "nav.hub", icon: () => <Users size={18} aria-hidden="true" /> },
  { view: "guide", labelKey: "nav.guide", icon: () => <HelpCircle size={18} aria-hidden="true" /> },
  { view: "settings", labelKey: "nav.settings", icon: () => <SettingsIcon size={18} aria-hidden="true" /> },
];

export function Sidebar() {
  const view = useUiStore((s) => s.view);
  const setView = useUiStore((s) => s.setView);
  const setSidebarOpen = useUiStore((s) => s.setSidebarOpen);
  const { t } = useT();

  const select = (v: AppView) => {
    setView(v);
    setSidebarOpen(false);
  };

  return (
    <nav className="flex h-full flex-col">
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-4">
        <span className="min-w-0 truncate text-sm font-bold text-accent-soft">RoQeet Launcher</span>
      </div>

      <ul className="flex-1 overflow-y-auto p-2 scroll-touch">
        {NAV.map((item) => {
          const active = item.view === view;
          const Icon = item.icon;
          return (
            <li key={item.view}>
              <button
                className={[
                  "mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors",
                  active
                    ? "bg-accent/20 text-ink"
                    : "text-ink-soft hover:bg-bg-raised hover:text-ink",
                ].join(" ")}
                aria-current={active ? "page" : undefined}
                onClick={() => select(item.view)}
              >
                <Icon />
                {t(item.labelKey)}
              </button>
            </li>
          );
        })}
      </ul>

      {/* 언어 선택기: 항상 보임. 어느 언어 사용자든 자국어 이름으로 즉시 찾는다. */}
      <LanguagePicker />

      <div className="shrink-0 border-t border-line px-3 py-2 text-xs text-ink-faint">
        {t("app.tagline")}
      </div>
    </nav>
  );
}

/** 사이드바 하단 언어 드롭다운. Settings.locale을 직접 읽고 쓴다(반응형은 useT가 처리). */
function LanguagePicker() {
  const [settings, setSettings] = useState<Settings | undefined>();
  useEffect(() => {
    void storage.getSettings().then(setSettings);
  }, []);

  const current = normalizeLocale(settings?.locale);
  const change = async (locale: string) => {
    const s = settings ?? (await storage.getSettings());
    if (!s) return;
    const next = { ...s, locale } as Settings;
    setSettings(next);
    await storage.putSettings(next);
  };

  return (
    <div className="shrink-0 border-t border-line p-2">
      <label className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-ink-soft">
        <Globe size={16} aria-hidden="true" className="shrink-0" />
        <select
          className="min-w-0 flex-1 cursor-pointer rounded-md border border-line bg-bg-soft px-2 py-1 text-sm text-ink outline-none focus:border-accent"
          value={current}
          onChange={(e) => void change(e.target.value)}
          aria-label="Language"
        >
          {LOCALES.map((l) => (
            <option key={l.code} value={l.code}>
              {l.nativeName}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
